import { createRouter, createWebHistory } from 'vue-router';
import { redirectAdminSection } from '@/services/utils/adminSections';
import emitter from '@/eventBus';
import user from '@/services/Auth/authInstance';

import HomePage from '@/views/HomePage/HomePage.vue';
const NotFound = () => import('@/views/NotFound/NotFound.vue');
const AllClubs = () => import('@/views/AllClubs/AllClubs.vue');
const Features = () => import('@/views/Public/Features/Features.vue');
const Pricing = () => import('@/views/Public/Pricing/Pricing.vue');
const About = () => import('@/views/Public/About/About.vue');
const Changelog = () => import('@/views/Public/Changelog/Changelog.vue');
const Chess = () => import('@/views/Public/Chess/Chess.vue');
const PublicSearch = () => import('@/views/Public/Search/Search.vue');
const SetPassword = () => import('@/views/Public/SetPassword/SetPassword.vue');
const ClubLayout = () => import('@/components/layout/ClubLayout/ClubLayout.vue');
const ClubHome = () => import('@/views/ClubHome/ClubHome.vue');
const NewTournament = () => import('@/views/NewTournament/NewTournament.vue');
const AllTournaments = () => import('@/views/AllTournaments/AllTournaments.vue');
const AllPlayers = () => import('@/views/AllPlayers/AllPlayers.vue');
const Accounting = () => import('@/views/AdminTool/Accounting/Accounting.vue');
const AdminTool = () => import('@/views/AdminTool/AdminTool.vue');
const TournamentView = () => import('@/views/Tournament/Tournament.vue');
const TournamentInfo = () => import('@/views/Tournament/TournamentTabs/InfoTournament/InfoTournament.vue');
const TournamentPairing = () => import('@/views/Tournament/TournamentTabs/PairingsTournament/PairingsTournament.vue');
const TournamentStanding = () => import('@/views/Tournament/TournamentTabs/StandingsTournament/StandingsTournament.vue');
const NewSeason = () => import('@/views/NewSeason/NewSeason.vue');
const AllSeasons = () => import('@/views/AllSeasons/AllSeasons.vue');
const SeasonView = () => import('@/views/Season/Season.vue');
const InfoSeason = () => import('@/views/Season/SeasonTabs/InfoSeason/InfoSeason.vue');
const OverviewSeason = () => import('@/views/Season/SeasonTabs/OverviewSeason/OverviewSeason.vue');
const TournamentsSeason = () => import('@/views/Season/SeasonTabs/TournamentsSeason/TournamentsSeason.vue');
const LeaderboardSeason = () => import('@/views/Season/SeasonTabs/LeaderboardSeason/LeaderboardSeason.vue');
const AdminToolImportTab = () => import('@/views/AdminTool/Import/Import.vue');
const AdminToolMergeTab = () => import('@/views/AdminTool/MergePlayers/MergePlayersTool.vue');
const AdminToolRatingTab = () => import('@/views/AdminTool/RatingManager/RatingManager.vue');
const AdminToolSeasonTab = () => import('@/views/AdminTool/SeasonManager/SeasonManager.vue');
const ClubPlayers = () => import('@/views/AdminTool/ClubPlayers/ClubPlayers.vue');
const ClubSettings = () => import('@/views/AdminTool/ClubSettings/ClubSettings.vue');

import { makeLogger } from '@/services/utils/logger';
import { LOGGING } from '@/config';
import { LOCALE_CHANGED_EVENT, translate } from '@/i18n';
import { updatePageSeo } from '@/services/seo';
import { localeFromPath } from '@/seoConfig';
import { notFoundPageProps } from '@/services/utils/notFoundPage';
import { useClubStore } from '@/stores/club';
import { useDataSourceStore } from '@/stores/dataSource';
import MockService from '@/services/MockService';
import ClubService from '@/services/api/models/ClubService';

const DEBUG_LOG = false;
const log = makeLogger('RouterGuard', DEBUG_LOG);

function redirectLegacyClubRoute(to) {
  const clubSlug = to.params?.clubSlug;
  const rawPath = to.params?.pathMatch;
  const segments = (Array.isArray(rawPath) ? rawPath : String(rawPath || '').split('/'))
    .filter(Boolean);
  const location = { query: to.query, hash: to.hash };

  if (segments[0] === 'tournament' && segments[1]) {
    const tail = segments.slice(2).join('/');
    if (tail) return { ...location, path: `/${clubSlug}/${segments[1]}/${tail}` };

    return {
      ...location,
      name: 'TournamentMain',
      params: { clubSlug, id: segments[1] },
    };
  }

  if (segments[0] === 'season' && segments[1]) {
    const tail = segments.slice(2).join('/');
    if (tail) return { ...location, path: `/${clubSlug}/s/${segments[1]}/${tail}` };

    return {
      ...location,
      name: 'SeasonMain',
      params: { clubSlug, slug: segments[1] },
    };
  }

  if (segments.length === 0) {
    return { ...location, name: 'ClubHome', params: { clubSlug } };
  }

  return { ...location, path: `/${clubSlug}/${segments.join('/')}` };
}

function redirectTournamentAlias(to) {
  const tail = Array.isArray(to.params?.pathMatch)
    ? to.params.pathMatch.join('/')
    : String(to.params?.pathMatch || '');

  return {
    path: `/${to.params.clubSlug}/${to.params.id}${tail ? `/${tail}` : ''}`,
    query: to.query,
    hash: to.hash,
  };
}

function redirectSeasonAlias(to) {
  const tail = Array.isArray(to.params?.pathMatch)
    ? to.params.pathMatch.join('/')
    : String(to.params?.pathMatch || '');

  return {
    path: `/${to.params.clubSlug}/s/${to.params.slug}${tail ? `/${tail}` : ''}`,
    query: to.query,
    hash: to.hash,
  };
}

const routes = [
  { path: '/', name: 'Home', component: HomePage, meta: { titleKey: 'routeTitles.home' } },
  { path: '/clubs/', name: 'AllClubs', component: AllClubs, meta: { titleKey: 'routeTitles.clubs' } },
  { path: '/features/', name: 'Features', component: Features, meta: { titleKey: 'routeTitles.features' } },
  { path: '/pricing/', name: 'Pricing', component: Pricing, meta: { titleKey: 'routeTitles.pricing' } },
  { path: '/about/', name: 'About', component: About, meta: { titleKey: 'routeTitles.about' } },
  { path: '/changelog/', name: 'Changelog', component: Changelog, meta: { titleKey: 'routeTitles.changelog' } },
  { path: '/chess/960/', name: 'Chess', component: Chess, meta: { titleKey: 'routeTitles.chess' } },
  { path: '/search', name: 'PublicSearch', component: PublicSearch, meta: { titleKey: 'routeTitles.search' } },
  { path: '/set-password/', name: 'SetPassword', component: SetPassword, meta: { titleKey: 'passwordSetup.title', noindex: true } },
  {
    path: '/:clubSlug',
    name: 'Club',
    component: ClubLayout,
    meta: { titleKey: 'routeTitles.club' },
    children: [
      { path: '', name: 'ClubHome', component: ClubHome, meta: { titleKey: 'routeTitles.club' } },
      { path: 'players', name: 'AllPlayers', component: AllPlayers, meta: { titleKey: 'routeTitles.allPlayers' } },
      { path: 'tournaments', name: 'AllTournaments', component: AllTournaments, meta: { titleKey: 'routeTitles.allTournaments' } },
      { path: 'seasons', name: 'AllSeasons', component: AllSeasons, meta: { titleKey: 'routeTitles.allSeasons' } },
      { path: 'tournaments/new', name: 'NewTournament', component: NewTournament, meta: { titleKey: 'routeTitles.newTournament', requiresAuth: true, requiresOwnClub: true } },
      { path: 'seasons/new', name: 'NewSeason', component: NewSeason, meta: { titleKey: 'routeTitles.newSeason', requiresAuth: true, requiresOwnClub: true } },
      {
        path: 'admin-tool',
        name: 'AdminTool',
        component: AdminTool,
        meta: { titleKey: 'routeTitles.adminTools', requiresAuth: true, requiresOwnClub: true, hidden: true },
        children: [
          { path: '', name: 'AdminToolMain', redirect: { name: 'AdminToolPlayers' } },

          // Tabs as nested routes (so URL changes when switching tab)
          { path: 'templates', name: 'AdminToolTemplates', redirect: to => redirectAdminSection('templates', to) },
          { path: 'templates/:section(nominations|places|tournaments)', name: 'AdminToolTemplatesSection', component: () => import('@/views/AdminTool/Templates/Templates.vue'), meta: { titleKey: 'adminTemplates.title', adminSectionGroup: 'templates' } },
          { path: 'settings', name: 'AdminToolSettings', component: ClubSettings, meta: { titleKey: 'clubSettings.title' } },
          { path: 'accounting', name: 'AdminToolAccounting', redirect: to => redirectAdminSection('accounting', to) },
          { path: 'accounting/:section(tournaments|subscriptions|rules)', name: 'AdminToolAccountingSection', component: Accounting, meta: { titleKey: 'accounting.title', adminSectionGroup: 'accounting' } },
          { path: 'import', name: 'AdminToolImport', component: AdminToolImportTab, meta: { titleKey: 'routeTitles.import' } },
          { path: 'merge', name: 'AdminToolMerge', component: AdminToolMergeTab, meta: { titleKey: 'routeTitles.mergePlayers' } },
          { path: 'players', name: 'AdminToolPlayers', redirect: to => redirectAdminSection('players', to) },
          { path: 'players/:section(list|labels|categories|titles)', name: 'AdminToolPlayersSection', component: ClubPlayers, meta: { titleKey: 'routeTitles.clubPlayers', adminSectionGroup: 'players' } },
          { path: 'rating', name: 'AdminToolRating', component: AdminToolRatingTab, meta: { titleKey: 'routeTitles.ratings' } },
          { path: 'season', name: 'AdminToolSeason', component: AdminToolSeasonTab, meta: { titleKey: 'routeTitles.seasons' } },
        ],
      },
      {
        path: 's/:slug',
        name: 'SeasonView',
        component: SeasonView,
        meta: { titleKey: 'routeTitles.season' },
        children: [
          { path: '', name: 'SeasonMain', redirect: to => ({ name: 'SeasonOverview', params: to.params }) },
          { path: 'ovreview', name: 'SeasonOverview', component: OverviewSeason, meta: { titleKey: 'routeTitles.seasonOverview' } },
          { path: 'info', name: 'SeasonInfo', component: InfoSeason, meta: { titleKey: 'routeTitles.seasonInfo' } },
          { path: 'tournaments', name: 'SeasonTournaments', component: TournamentsSeason, meta: { titleKey: 'routeTitles.seasonTournaments' } },
          { path: 'leaderboard', name: 'SeasonLeaderboard', component: LeaderboardSeason, meta: { titleKey: 'routeTitles.seasonLeaderboard' } },
          { path: ':pathMatch(.*)*', name: 'SeasonRouteNotFound', component: NotFound, meta: { title: '404' } },
        ]
      },
      {
        path: 'season/:slug/:pathMatch(.*)*',
        redirect: redirectSeasonAlias,
      },
      {
        path: 't/:id/:pathMatch(.*)*',
        redirect: redirectTournamentAlias,
      },
      {
        path: 'tournament/:id/:pathMatch(.*)*',
        redirect: redirectTournamentAlias,
      },
      {
        path: ':id',
        name: 'TournamentView',
        component: TournamentView,
        meta: { titleKey: 'routeTitles.tournament' },
        children: [
          { path: '', name: 'TournamentMain', redirect: to => ({ name: 'TournamentInfo', params: to.params }) },
          { path: 'info', name: 'TournamentInfo', component: TournamentInfo, meta: { titleKey: 'routeTitles.tournamentInfo' } },
          { path: 'pairings', name: 'TournamentPairing', component: TournamentPairing, meta: { titleKey: 'routeTitles.pairings' } },
          { path: 'standings', name: 'TournamentStanding', component: TournamentStanding, meta: { titleKey: 'routeTitles.standings' } },
          { path: ':pathMatch(.*)*', name: 'TournamentRouteNotFound', component: NotFound, meta: { title: '404' } },
        ]
      },
      {
        path: ':pathMatch(.*)*',
        name: 'ClubNotFound',
        component: NotFound,
        meta: { title: '404' },
      },
    ],
  },
  {
    path: '/clubs/:clubSlug/:pathMatch(.*)*',
    name: 'LegacyClubsRoute',
    redirect: redirectLegacyClubRoute,
  },
  {
    path: '/club/:clubSlug/:pathMatch(.*)*',
    name: 'LegacyClubRoute',
    redirect: redirectLegacyClubRoute,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { title: '404' },
  },
];



// Статус 404 определён сервером. Показываем его внутри обычного App, не загружая
// ошибочную сущность повторно и не перенаправляя исходный адрес при инициализации.
const initialHttpError = document.querySelector('meta[name="app-error-page"]')?.content === '404';
if (initialHttpError) {
  const kind = document.querySelector('meta[name="app-error-kind"]')?.content || 'page';
  const rawClub = document.querySelector('meta[name="app-error-club"]')?.content || '';
  const clubSlug = rawClub === '__ERROR_CLUB__' ? '' : decodeURIComponent(rawClub);
  const locale = localeFromPath(window.location.pathname);
  const path = (locale === 'ru' ? window.location.pathname.slice(3) : window.location.pathname) || '/';
  let hasClubParam = false;
  const errorPath = path.split('/').map(segment => {
    if (!hasClubParam && clubSlug && decodeURIComponent(segment) === clubSlug) {
      hasClubParam = true;
      // Узкий параметр сохраняет контекст Navbar и совпадает только с этим адресом.
      return `:clubSlug(${segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`;
    }
    return segment.replace(/:/g, '\\:');
  }).join('/');
  routes.unshift({
    path: errorPath, name: 'InitialHttpNotFound', component: NotFound,
    meta: { title: '404', errorClubSlug: ['tournament', 'season'].includes(kind) ? clubSlug : null },
    // Языковой префикс к RouterLink добавляет createWebHistory, а не backTo.
    props: () => notFoundPageProps(kind, translate, clubSlug, 'en'),
    beforeEnter() { useClubStore().clearCurrentClub(); },
  });
}

const router = createRouter({
  history: createWebHistory(localeFromPath(window.location.pathname) === 'ru' ? '/ru/' : '/'),
  routes,
  scrollBehavior(to, from) {
    // Лента сама выбирает страницу и прокручивает к выпуску после отрисовки.
    if (to.name === 'Changelog' && typeof to.query.release === 'string') return false;
    if (to.hash) {
      return {
        el: to.hash,
        top: 96,
        behavior: 'smooth',
      };
    }

    if (to.path === from.path) {
      return false;
    }

    return { top: 0 };
  },
});

/**
 * Обновляет заголовок страницы после каждого перехода по маршрутам.
 * @param {Object} to - Объект маршрута, к которому осуществляется переход.
 */
function updateDocumentTitle(route) {
  updatePageSeo(route);
}

router.afterEach((to, from, failure) => {
  if (!failure && to.name === 'InitialHttpNotFound' && to.meta.errorClubSlug) {
    const store = useClubStore();
    // Страница ошибки уже открыта; клуб нужен только для оформления навбара.
    // Поздний ответ не должен менять клуб после перехода на другую страницу.
    const clubSlug = to.meta.errorClubSlug;
    const sourceStore = useDataSourceStore();
    const revision = sourceStore.revision;
    Promise.resolve(store.getBySlug(clubSlug) || (sourceStore.isMock
      ? MockService.getClubBySlug(clubSlug)
      : ClubService.getBySlug(clubSlug)))
      .then(club => {
        if (club && router.currentRoute.value === to && sourceStore.revision === revision) store.setCurrentClub(club);
      })
      .catch(error => log.warn('http-error-club', 'Не удалось загрузить клуб для страницы ошибки', error));
  }
  if (!failure && from.name === 'InitialHttpNotFound' && to.name !== 'InitialHttpNotFound') {
    router.removeRoute('InitialHttpNotFound');
    useClubStore().clearCurrentClub();
    document.querySelectorAll('meta[name^="app-error-"]').forEach(element => element.remove());
  }
  if (!failure) updateDocumentTitle(to);
});

window.addEventListener(LOCALE_CHANGED_EVENT, () => {
  // Инициализация языка происходит до первого перехода: стартовый маршрут
  // ещё равен '/', независимо от реально открытого адреса.
  if (router.currentRoute.value.matched.length) updateDocumentTitle(router.currentRoute.value);
});

/**
 * Защищает маршруты, требующие авторизации.
 * Если пользователь не авторизован, открывает диалог входа.
 * @param {Object} to - Целевой маршрут.
 * @param {Object} from - Исходный маршрут.
 * @param {Function} next - Функция продолжения навигации.
 */

router.beforeEach((to, from, next) => {
  log('beforeEach', 'navigation start', {
    to: { name: to.name, params: to.params, meta: to.meta },
    from: { name: from.name, params: from.params }
  });

  if (to.path !== from.path) {
    disableSaveScroll();
  }

  const requiresAuth = to.meta.requiresAuth;

  log('auth-check', 'requiresAuth check', {
    requiresAuth,
    isAuthenticated: user.isAuthenticated.value
  });

  if (requiresAuth && !user.isAuthenticated.value) {
    sessionStorage.setItem('redirectAfterLogin', to.fullPath);
    log.warn('auth-check', 'user not authenticated, opening login dialog', {
      target: to.fullPath
    });
    emitter.emit('open-login-dialog'); // прямой вызов
    if (!from.name) {
      // Прямой переход (например, через URL в адресной строке)
      return next({ path: '/', replace: true });
    } else {
      // Навигация внутри приложения — не переходить
     return next(false);
    }

    //return next(false); // остановка перехода
  }

  if (handleOwnClubGuard(to, next)) {
    return;
  }

  log('beforeEach', 'navigation allowed');
  next();
});

/**
 * Guard: routes with requiresOwnClub can be accessed only
 * inside user's own club.
 */
function handleOwnClubGuard(to, next) {
  if (!to.meta?.requiresOwnClub) return false;
  if (!user.isAuthenticated.value) return false;

  const userClubSlug = user.clubSlug;
  const routeClubSlug = to.params?.clubSlug;

  // Если у пользователя нет клуба — уводим к списку клубов
  if (!userClubSlug) {
    next({ name: 'AllClubs' });
    return true;
  }

  // Если пытаются создать сущность в чужом клубе — редирект в свой
  if (routeClubSlug && routeClubSlug !== userClubSlug) {
    // ВАЖНО: не spread'им `to.params`, иначе Vue Router (v4.1.4+) будет ругаться,
    // что переданы params, которых нет в целевом маршруте.
    // Для наших requiresOwnClub-роутов достаточно только clubSlug.
    next({ name: to.name, params: { clubSlug: userClubSlug }, replace: true });
    return true;
  }

  return false;
}

function disableSaveScroll() {
  // На всякий случай отключим любую «плавность», если где-то задана
  document.documentElement.style.scrollBehavior = 'auto';

  // Синхронно ставим страницу в (0,0) до начала навигации
  window.scrollTo(0, 0);
  // также продублируем для разных движков
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

/**
 * Экспортирует настроенный экземпляр маршрутизатора.
 */
export default router;
